import React, { useState, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { RequiredAsterisk } from '../../../components/common/RequiredAsterisk';

interface MultiSelectProps {
  label?: string;
  options: string[];
  value?: string[] | null;
  onChange: (value: string[]) => void;
  required?: boolean;
  placeholder?: string;
  maxSelections?: number;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  name?: string;
  displayMap?: Record<string, string>; // Optional mapping from option value to display text
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value = [],
  onChange,
  required,
  placeholder = "Select options",
  maxSelections = Infinity,
  disabled = false,
  error,
  touched,
  name,
  displayMap
}) => {

  // Helper function to get display text for an option
  const getDisplayText = (option: string) => {
    return displayMap && displayMap[option] ? displayMap[option] : option;
  };

  
  const safeValue = value ?? [];
  
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasError = error && touched;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard typing to filter options
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen || disabled) return;

      // Handle typing for search
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setSearchTerm(prev => prev + e.key);
      }
      // Handle backspace
      if (e.key === 'Backspace') {
        setSearchTerm(prev => prev.slice(0, -1));
      }
      // Handle escape
      if (e.key === 'Escape') {
        setSearchTerm('');
        setIsOpen(false);
      }
      // Handle Enter to select first filtered option
      if (e.key === 'Enter' && filteredOptions.length > 0) {
        e.preventDefault();
        const firstOption = filteredOptions[0];
        if (!safeValue.includes(firstOption)) {
          handleOptionClick(firstOption);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, disabled, searchTerm]);

  // Clear search when closing dropdown
  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  // Handles option selection/deselection and enforces selection limit
  const handleOptionClick = useCallback((optionValue: string) => {
    
    if (disabled) return;
    
    const isSelected = safeValue.includes(optionValue);

    if (!isSelected && safeValue.length >= maxSelections) {
      Swal.fire({
        icon: "warning",
        title: "Selection Limit Reached",
        text: `You can only select up to ${maxSelections} item${maxSelections > 1 ? 's' : ''}.`,
      });
      return;
    }

    const newValue = isSelected
      ? safeValue.filter(v => v !== optionValue)
      : [...safeValue, optionValue];

    onChange(newValue);
    
   
    
    // Clear search term after selection to show full list
    setSearchTerm('');
  }, [disabled, safeValue, maxSelections, onChange]);
  

  // Filter options based on search term (search on display text)
  const filteredOptions = options.filter(option =>
    getDisplayText(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Determine display text for the select button
  const displayValue = safeValue.length ?
    safeValue.length > 2 ?
      `${safeValue.filter(opt=>opt != "Select All").slice(0, 2).map(getDisplayText).join(', ')} +${safeValue.length - 2} more` :
      safeValue.map(getDisplayText).join(', ')
    : placeholder;

  // Handle clear all selections
  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange([]);
    }
  };
  const handleSelectAll = () => {
    const actualOptions = filteredOptions.filter(opt => opt !== "SelectAll");
  
    const allSelected = actualOptions.every(opt => safeValue.includes(opt));
  
    if (allSelected) {
      // Unselect all actual options (not including "SelectAll")
      const remaining = safeValue.filter(val => !actualOptions.includes(val));
      onChange(remaining);
    } else {
      // Select all actual options (up to maxSelections if applicable)
      const remainingSlots = maxSelections ? maxSelections - safeValue.length : Infinity;
      const newSelections = actualOptions.filter(opt => !safeValue.includes(opt)).slice(0, remainingSlots);
      onChange([...safeValue, ...newSelections]);
    }
  };
  

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          {label}
          {required && <RequiredAsterisk />}
        </label>
      )}

      <div className={`relative w-full ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {/*
          Neutral at rest, primary once the control is engaged — hovered, open,
          or focused. The open state is set directly rather than left to
          focus-within: `hover:` and `focus-within:` share a specificity and
          Tailwind emits hover last, so hover would otherwise win over open.
        */}
        <div
          className={`flex justify-between items-center px-3 py-2 text-sm border rounded-md bg-white cursor-pointer transition-colors ${
            hasError
              ? 'border-danger-500'
              : isOpen
                ? 'border-primary-500'
                : 'border-neutral-300 hover:border-primary-500 focus-within:border-primary-500'
          } ${disabled ? 'cursor-not-allowed' : ''}`}
          onClick={() => !disabled && setIsOpen(prev => !prev)}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label || 'Multi-select dropdown'}
        >
          
          <div
            className="text-sm text-neutral-700 placeholder-neutral-500 truncate"
            title={safeValue.filter(opt => opt != "Select All").map(getDisplayText).join(', ')}
          >
            <span className={safeValue.length ? 'text-neutral-700' : 'text-neutral-500'}>
              {displayValue}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {safeValue.length > 0 && !disabled && (
              <button
                type="button"
                className="flex items-center justify-center w-4 h-4 text-sm font-bold leading-none text-neutral-400 transition-colors rounded-full hover:text-primary-700 hover:bg-primary-50"
                onClick={handleClearAll}
                title="Clear all selections"
                aria-label="Clear all selections"
              >
                ×
              </button>
            )}
            <span className={`text-neutral-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </div>

        {isOpen && !disabled && (
          <div className="absolute left-0 right-0 z-50 mt-1 overflow-y-auto bg-white border border-neutral-200 rounded-md shadow-lg top-full max-h-48" role="listbox">
            {/* Show current search term if user is typing */}
            {searchTerm && (
              <div className="p-2 text-sm text-neutral-600 border-b border-neutral-200 bg-neutral-50">
                Searching for: "<strong>{searchTerm}</strong>"
                {filteredOptions.length > 0 && (
                  <span className="ml-1 text-neutral-500">
                    ({filteredOptions.length} result{filteredOptions.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            )}

            {/* Selection counter */}
            {maxSelections !== Infinity && (
              <div className="p-2 text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                {safeValue.length} / {maxSelections} selected
              </div>
            )}

            <div className="py-1">
              {filteredOptions.length > 0 ? (
                 filteredOptions.map((option) => (
                  option==="Select All" ? (

                 <div
                 className={`flex items-center px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-neutral-50 ${
                  filteredOptions
                    .filter(opt => opt !== "Select All")
                    .every(opt => safeValue.includes(opt))
                    ? 'bg-primary-50 text-primary-900'
                    : 'text-neutral-900'
                }`} 
                 onClick={handleSelectAll}
               >
                 <input
                   type="checkbox"
                   readOnly
                   
                   checked={filteredOptions.filter(opt => opt !== "Select All").every(opt => safeValue.includes(opt))}
                   className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                 />
                <span className="flex-1 text-sm text-neutral-700">{getDisplayText(option)}</span>
               </div>):(
                  <div
                    key={option}
                    className={`flex items-center px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-neutral-50 ${
                      safeValue.includes(option) ? 'bg-primary-50 text-primary-900' : 'text-neutral-900'
                    }`}
                    onClick={() => handleOptionClick(option)}
                    role="option"
                    aria-selected={safeValue.includes(option)}
                  >
                    <input
                      type="checkbox"
                      checked={safeValue.includes(option)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleOptionClick(option)}
                      className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                      tabIndex={-1}
                      aria-hidden="true"
                      required={required} 
                    />
                    <span className="flex-1 text-sm text-neutral-700">{getDisplayText(option)}</span>
                  </div>)
                ))
                
              ) : (
                <div className="px-3 py-2 text-sm text-neutral-500">
                  {searchTerm ? (
                    <>
                      No results for "<strong>{searchTerm}</strong>"
                      <div className="mt-1 text-xs text-neutral-400">Try a different search term</div>
                    </>
                  ) : (
                    'No options available'
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {hasError && (
        <div className="mt-1 text-sm text-danger-600" role="alert">
          {error}
        </div>
      )}

      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={safeValue.join(',')}
        />
      )}
    </div>
  );
};

export default MultiSelect;
